import React, { useState, useEffect } from 'react';
import { debounce } from 'lodash';
import { Input } from './components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from './components/ui/card';
import { CircularProgress } from './components/ui/progress';

const API_URL = import.meta.env.VITE_API_URL;

const OutboundSearch = () => {
  const [outbounds, setOutbounds] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [debouncing, setDebouncing] = useState(false);

  useEffect(() => {
    const fetchOutbounds = async () => {
      if (searchTerm.length > 2) {
        setLoading(true);
        setDebouncing(false);
        try {
          const response = await fetch(`${API_URL}/api/get_outbounds?searchTerm=${encodeURIComponent(searchTerm)}`);
          const data = await response.json();
          // Sort by UPDATED_AT in descending order (Z-A)
          const sortedData = data.sort((a, b) => new Date(b.UPDATED_AT) - new Date(a.UPDATED_AT));
          setOutbounds(sortedData);
        } catch (error) {
          console.error('Error fetching outbounds:', error);
        } finally {
          setLoading(false);
        }
      } else {
        setOutbounds([]);
        setLoading(false);
      }
    };

    const debouncedFetch = debounce(() => {
      setDebouncing(false);  // Debouncing is done, start fetching
      fetchOutbounds();
    }, 700); // Debounce by 1 second

    setDebouncing(true);
    debouncedFetch();

    return () => {
      debouncedFetch.cancel();
    };
  }, [searchTerm]);

  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-8">
        <Input
          placeholder="Search internal notes or client notes"
          value={searchTerm}
          onChange={handleSearch}
          className="w-full"
        />
      </div>

      {loading || debouncing ? (
        <div className="flex justify-center my-8">
          <CircularProgress size={32} />
        </div>
      ) : (
        <>
          {outbounds.length === 0 && searchTerm.length > 2 ? (
            <div className="flex justify-center my-8">
              <h2 className="text-xl text-muted-foreground">
                No results found for "{searchTerm}" in any notes.
              </h2>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {outbounds.map((outbound) => (
                <Card key={outbound.ID} className="hover:shadow-md transition-shadow">
                  <CardHeader>
                    <CardTitle>
                      <a
                        href={`https://app.yyzprep.ca/staff/shipments/outbound/${outbound.ID}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary hover:underline"
                      >
                        {outbound.NAME}
                      </a>
                    </CardTitle>
                    <p className="text-sm text-muted-foreground">
                      Created: {new Date(outbound.CREATED_AT).toLocaleDateString()} | Updated: {new Date(outbound.UPDATED_AT).toLocaleDateString()}
                    </p>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <p className="text-sm text-muted-foreground">
                      ID: {outbound.ID}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      Internal Notes: {outbound.INTERNAL_NOTES}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      Client Notes: {outbound.CLIENT_NOTES}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default OutboundSearch;
